import { FC, HTMLAttributes, MouseEventHandler, ReactNode, useCallback, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import { PositioningStrategy } from '@popperjs/core';
import { useOnClickOutside } from '@/hooks';
import { getSmallPopoverOptions, useMediumPopoverBodyLock, useOnCloseRef } from './Popover.shared';
import { useAdaptivePointerOffset } from './useAdaptivePointerOffset';
import { MediumPopoverView, SmallPopoverView } from './Popover.views';
import styles from './Popover.module.css';

export enum PopoverAppearance {
  'primary' = 'primary',
  'secondary' = 'secondary',
}

export enum NoticePosition {
  'top' = 'top',
  'bottom' = 'bottom',
  'right' = 'right',
  'right-start' = 'right-start',
  'right-end' = 'right-end',
  'left' = 'left',
  'top-left' = 'top-left',
  'top-right' = 'top-right',
  'bottom-left' = 'bottom-left',
  'bottom-right' = 'bottom-right',
}

export type PopoverSize = 'M' | 'S';

export type PopoverPointer = 'none' | 'top' | 'bottom' | 'left' | 'right';
export type PopoverTrigger = 'click' | 'hover';
export type PopoverLayer = 'default' | 'below-header';

export type PopoverProps = {
  'title'?: string;
  'noticeTitleProps'?: HTMLAttributes<HTMLHeadingElement>;
  'appearance'?: keyof typeof PopoverAppearance;
  'noticePosition'?: keyof typeof NoticePosition;
  'disableAutoPosition'?: boolean;
  'closeOnNoticeContentClick'?: boolean;
  'withCloseButton'?: boolean;
  'onActiveFn'?: VoidFunction;
  'anchorContent': ReactNode;
  'noticeContent': ReactNode;
  'data-testid'?: string;
  'overlayingNoticeTestId'?: string;
  'className'?: string;
  'headerClassName'?: string;
  'disableCloseOnClickOutside'?: boolean;
  'defaultIsOpen'?: boolean;
  'disabled'?: boolean;
  'horizontalOffset'?: number;
  'verticalOffset'?: number;
  'isOpen'?: boolean;
  'onClose'?: VoidFunction;
  'onOpenChange'?: (isOpen: boolean) => void;
  'size'?: PopoverSize;
  'pointer'?: PopoverPointer;
  'portal'?: boolean;
  'fallbackPlacements'?: Array<keyof typeof NoticePosition>;
  'layer'?: PopoverLayer;
  'positioningStrategy'?: PositioningStrategy;
  'raw'?: boolean;
  'trigger'?: PopoverTrigger;
};

const POINTER_TO_NOTICE_POSITION: Record<Exclude<PopoverPointer, 'none'>, keyof typeof NoticePosition> = {
  top: 'top',
  bottom: 'bottom',
  left: 'left',
  right: 'right',
};

const PLACEMENT_TO_POINTER_EDGE: Record<string, Exclude<PopoverPointer, 'none'>> = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
};

const Popover: FC<PopoverProps> = ({
  anchorContent,
  noticeContent,
  title,
  noticeTitleProps,
  'appearance': _appearance = 'primary',
  noticePosition = 'top',
  'disableAutoPosition': _disableAutoPosition = false,
  closeOnNoticeContentClick = false,
  withCloseButton = true,
  onActiveFn,
  'data-testid': dataTestId = 'popover',
  overlayingNoticeTestId = 'popover-overlaying-notice',
  className,
  headerClassName,
  disableCloseOnClickOutside,
  defaultIsOpen = false,
  disabled = false,
  'horizontalOffset': _horizontalOffset = 0,
  'verticalOffset': _verticalOffset = 0,
  'isOpen': controlledIsOpen,
  onClose,
  onOpenChange,
  size = 'S',
  pointer = 'none',
  portal = true,
  fallbackPlacements,
  layer = 'default',
  positioningStrategy = 'fixed',
  raw = false,
  trigger = 'click',
}) => {
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState<boolean>(defaultIsOpen);
  const [anchorElement, setAnchorElement] = useState<HTMLSpanElement | null>(null);
  const [panelElement, setPanelElement] = useState<HTMLDivElement | null>(null);
  const anchorTargetRef = useRef<HTMLSpanElement | null>(null);
  const hasPointer = pointer !== 'none';
  const useRawContent = raw || !Boolean(title || withCloseButton);
  const isControlled = controlledIsOpen !== undefined;
  const isOverlayingNoticeActive = isControlled ? controlledIsOpen : uncontrolledIsOpen;
  const resolvedNoticePosition = hasPointer ? POINTER_TO_NOTICE_POSITION[pointer] : noticePosition;
  const onCloseRef = useOnCloseRef(onClose);
  const { styles: popperStyles, attributes: popperAttributes } = usePopper(
    anchorElement,
    panelElement,
    getSmallPopoverOptions({
      noticePosition: resolvedNoticePosition,
      horizontalOffset: _horizontalOffset,
      verticalOffset: _verticalOffset,
      disableAutoPosition: _disableAutoPosition,
      fallbackPlacements,
      hasPointer,
      positioningStrategy,
    }),
  );
  const popperPlacement = popperAttributes.popper?.['data-popper-placement'];
  const resolvedPointerEdge =
    typeof popperPlacement === 'string' ? PLACEMENT_TO_POINTER_EDGE[popperPlacement.split('-')[0]] : undefined;
  const pointerClassName = styles[`pointer--${resolvedPointerEdge ?? PLACEMENT_TO_POINTER_EDGE[pointer]}`];

  const setPopoverOpen = useCallback(
    (nextIsOpen: boolean) => {
      if (isOverlayingNoticeActive === nextIsOpen) return;
      if (!isControlled) setUncontrolledIsOpen(nextIsOpen);
      onOpenChange?.(nextIsOpen);
    },
    [isControlled, isOverlayingNoticeActive, onOpenChange],
  );
  const handleClose = useCallback(() => {
    setPopoverOpen(false);
    onCloseRef.current?.();
  }, [onCloseRef, setPopoverOpen]);
  const outsidePanelRef = useOnClickOutside(
    handleClose,
    size === 'S' && isOverlayingNoticeActive && !disableCloseOnClickOutside,
    { excludeRefs: [anchorTargetRef] },
  );

  const handleAnchorRef = useCallback((node: HTMLSpanElement | null) => {
    anchorTargetRef.current = node;
    setAnchorElement(node);
  }, []);
  const handlePanelRef = useCallback(
    (node: HTMLDivElement | null) => {
      outsidePanelRef.current = node;
      setPanelElement(node);
    },
    [outsidePanelRef],
  );
  const handleOnClick = useCallback(() => {
    if (disabled || trigger !== 'click') return;
    if (!isOverlayingNoticeActive && onActiveFn) onActiveFn();
    if (isOverlayingNoticeActive) handleClose();
    else setPopoverOpen(true);
  }, [disabled, handleClose, isOverlayingNoticeActive, onActiveFn, setPopoverOpen, trigger]);

  const handleMouseEnter = useCallback(() => {
    if (disabled || trigger !== 'hover') return;
    if (!isOverlayingNoticeActive) onActiveFn?.();
    // Re-entering a controlled hover panel must cancel its pending close timer.
    if (isControlled && isOverlayingNoticeActive) onOpenChange?.(true);
    else setPopoverOpen(true);
  }, [disabled, isControlled, isOverlayingNoticeActive, onActiveFn, onOpenChange, setPopoverOpen, trigger]);

  const handleMouseLeave = useCallback(() => {
    if (trigger !== 'hover') return;
    handleClose();
  }, [handleClose, trigger]);

  const handleBackdropClick = useCallback(() => {
    if (!disableCloseOnClickOutside) handleClose();
  }, [disableCloseOnClickOutside, handleClose]);

  const handleOnNoticeContentClick: MouseEventHandler<HTMLDivElement> = useCallback(() => {
    if (!closeOnNoticeContentClick) return;
    setPopoverOpen(false);
  }, [closeOnNoticeContentClick, setPopoverOpen]);
  useAdaptivePointerOffset({
    anchorElement,
    panelElement,
    hasPointer,
    isOpen: isOverlayingNoticeActive,
    pointerEdge: resolvedPointerEdge,
    size,
  });

  useMediumPopoverBodyLock(size, isOverlayingNoticeActive);

  const anchorViewProps = {
    anchorContent,
    className,
    dataTestId,
    isOpen: isOverlayingNoticeActive,
    onClick: handleOnClick,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
  };
  const panelContentProps = {
    headerClassName,
    noticeContent,
    noticeTitleProps,
    title,
    withCloseButton,
  };

  if (size === 'M') {
    return (
      <MediumPopoverView
        anchor={{ ...anchorViewProps, isOpen: !disabled && isOverlayingNoticeActive }}
        content={panelContentProps}
        isOpen={!disabled && isOverlayingNoticeActive}
        overlayingNoticeTestId={overlayingNoticeTestId}
        portal={portal}
        onBackdropClick={handleBackdropClick}
        onClose={handleClose}
        onContentClick={handleOnNoticeContentClick}
      />
    );
  }

  return (
    <SmallPopoverView
      anchor={{ ...anchorViewProps, anchorRef: handleAnchorRef }}
      content={panelContentProps}
      isOpen={isOverlayingNoticeActive}
      panel={{
        anchorElement: !disabled && isOverlayingNoticeActive ? anchorElement : null,
        dataTestId,
        hasPointer,
        isRaw: useRawContent,
        layer,
        panelAttributes: popperAttributes.popper ?? {},
        panelRef: handlePanelRef,
        pointerClassName,
        popperStyle: popperStyles.popper,
        portal,
      }}
      onClose={handleClose}
      onContentClick={handleOnNoticeContentClick}
    />
  );
};

export default Popover;
